import React from 'react';
import ChartRenderer, { IChartData } from './ChartRenderer';

interface MarkdownRendererProps {
    content?: string;
    isDark?: boolean;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content, isDark }) => {
    if (!content) return <span style={{ color: '#8c8c8c' }}>-</span>;

    const renderInline = (text: string) => {
        // Parse bold: **text**
        const tokenRegex = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
        const matches = text.split(tokenRegex);

        return matches.map((segment, idx) => {
            if (segment.startsWith('**') && segment.endsWith('**')) {
                return <strong key={idx}>{segment.slice(2, -2)}</strong>;
            }
            if (segment.startsWith('`') && segment.endsWith('`')) {
                return (
                    <code
                        key={idx}
                        style={{
                            background: isDark ? '#262626' : '#f0f0f0',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            fontFamily: 'monospace',
                        }}
                    >
                        {segment.slice(1, -1)}
                    </code>
                );
            }
            if (segment.startsWith('*') && segment.endsWith('*')) {
                return <em key={idx}>{segment.slice(1, -1)}</em>;
            }
            return segment;
        });
    };

    // Split text into blocks (paragraphs, headers, tables, codeblocks, blockquotes, lists)
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    while (i < lines.length) {
        const line = lines[i];

        // 1. Code Block: ```
        if (line.trim().startsWith('```')) {
            const lang = line.trim().slice(3).trim().toLowerCase();
            const codeLines: string[] = [];
            i++;
            while (i < lines.length && !lines[i].trim().startsWith('```')) {
                codeLines.push(lines[i]);
                i++;
            }
            i++; // skip closing ```

            const rawCode = codeLines.join('\n');

            // If it's a chart block, parse and render with ChartRenderer
            if (lang === 'chart') {
                try {
                    const parsedChart = JSON.parse(rawCode) as IChartData;
                    elements.push(
                        <ChartRenderer
                            key={`chart-${i}`}
                            chartData={parsedChart}
                            isDark={isDark}
                        />
                    );
                    continue;
                } catch (e) {
                    // Fallback to code block if invalid JSON
                }
            }

            elements.push(
                <div
                    key={`code-${i}`}
                    style={{
                        margin: '10px 0',
                        background: isDark ? '#141414' : '#f6f8fa',
                        border: `1px solid ${isDark ? '#303030' : '#e1e4e8'}`,
                        borderRadius: '6px',
                        padding: '10px 14px',
                        overflowX: 'auto',
                    }}
                >
                    {lang && (
                        <div
                            style={{
                                fontSize: '11px',
                                textTransform: 'uppercase',
                                color: '#8c8c8c',
                                marginBottom: '4px',
                                fontWeight: 600,
                            }}
                        >
                            {lang}
                        </div>
                    )}
                    <pre
                        style={{
                            margin: 0,
                            fontSize: '12px',
                            fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            color: isDark ? '#e6edf3' : '#24292e',
                        }}
                    >
                        {rawCode}
                    </pre>
                </div>
            );
            continue;
        }

        // 2. Table: lines starting and ending with |
        if (line.trim().startsWith('|') && line.trim().endsWith('|')) {
            const tableLines: string[] = [];
            while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
                tableLines.push(lines[i]);
                i++;
            }

            if (tableLines.length >= 2) {
                const parseRow = (rowStr: string) =>
                    rowStr
                        .trim()
                        .slice(1, -1)
                        .split('|')
                        .map((c) => c.trim());

                const headerRow = parseRow(tableLines[0]);
                const bodyRows = tableLines.slice(2).map(parseRow);

                elements.push(
                    <div
                        key={`table-${i}`}
                        style={{
                            overflowX: 'auto',
                            margin: '12px 0',
                            borderRadius: '6px',
                            border: `1px solid ${isDark ? '#303030' : '#e8e8e8'}`,
                        }}
                    >
                        <table
                            style={{
                                width: '100%',
                                borderCollapse: 'collapse',
                                fontSize: '12.5px',
                                textAlign: 'left',
                            }}
                        >
                            <thead>
                                <tr
                                    style={{
                                        background: isDark ? '#1f1f1f' : '#fafafa',
                                        borderBottom: `2px solid ${isDark ? '#303030' : '#e8e8e8'}`,
                                    }}
                                >
                                    {headerRow.map((h, colIdx) => (
                                        <th
                                            key={colIdx}
                                            style={{
                                                padding: '8px 12px',
                                                fontWeight: 600,
                                                color: isDark ? '#d9d9d9' : '#262626',
                                            }}
                                        >
                                            {renderInline(h)}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {bodyRows.map((row, rIdx) => (
                                    <tr
                                        key={rIdx}
                                        style={{
                                            borderBottom: `1px solid ${isDark ? '#262626' : '#f0f0f0'}`,
                                            background:
                                                rIdx % 2 === 1
                                                    ? isDark
                                                        ? '#171717'
                                                        : '#fafafa'
                                                    : 'transparent',
                                        }}
                                    >
                                        {row.map((cell, cIdx) => (
                                            <td
                                                key={cIdx}
                                                style={{
                                                    padding: '7px 12px',
                                                    color: isDark ? '#bfbfbf' : '#595959',
                                                }}
                                            >
                                                {renderInline(cell)}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                );
                continue;
            }
        }

        // 3. Headers: #, ##, ###
        if (line.startsWith('#')) {
            const match = line.match(/^(#{1,6})\s+(.*)$/);
            if (match) {
                const level = match[1].length;
                const text = match[2];
                const fontSize = level === 1 ? '15px' : level === 2 ? '14px' : '13px';
                elements.push(
                    <div
                        key={`h-${i}`}
                        style={{
                            fontWeight: 600,
                            fontSize,
                            margin: '14px 0 6px 0',
                            color: isDark ? '#f0f0f0' : '#262626',
                        }}
                    >
                        {renderInline(text)}
                    </div>
                );
                i++;
                continue;
            }
        }

        // 4. Blockquote: >
        if (line.startsWith('>')) {
            const quoteLines: string[] = [];
            while (i < lines.length && lines[i].startsWith('>')) {
                quoteLines.push(lines[i].replace(/^>\s?/, ''));
                i++;
            }
            elements.push(
                <blockquote
                    key={`quote-${i}`}
                    style={{
                        margin: '10px 0',
                        padding: '8px 14px',
                        borderLeft: `4px solid ${isDark ? '#1890ff' : '#1890ff'}`,
                        background: isDark ? '#111d2c' : '#e6f7ff',
                        borderRadius: '0 6px 6px 0',
                        fontSize: '13px',
                        color: isDark ? '#d9d9d9' : '#262626',
                    }}
                >
                    {quoteLines.map((q, qIdx) => (
                        <div key={qIdx}>{renderInline(q)}</div>
                    ))}
                </blockquote>
            );
            continue;
        }

        // 5. Unordered List: - or *
        if (line.trim().startsWith('- ') || line.trim().startsWith('* ')) {
            const listItems: string[] = [];
            while (
                i < lines.length &&
                (lines[i].trim().startsWith('- ') || lines[i].trim().startsWith('* '))
            ) {
                listItems.push(lines[i].trim().slice(2));
                i++;
            }
            elements.push(
                <ul
                    key={`ul-${i}`}
                    style={{
                        margin: '6px 0 10px 18px',
                        padding: 0,
                        fontSize: '13px',
                        lineHeight: '1.6',
                    }}
                >
                    {listItems.map((item, itemIdx) => (
                        <li key={itemIdx} style={{ marginBottom: '4px' }}>
                            {renderInline(item)}
                        </li>
                    ))}
                </ul>
            );
            continue;
        }

        // 6. Ordered List: 1.
        if (/^\d+\.\s+/.test(line.trim())) {
            const listItems: string[] = [];
            while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
                listItems.push(lines[i].trim().replace(/^\d+\.\s+/, ''));
                i++;
            }
            elements.push(
                <ol
                    key={`ol-${i}`}
                    style={{
                        margin: '6px 0 10px 20px',
                        padding: 0,
                        fontSize: '13px',
                        lineHeight: '1.6',
                    }}
                >
                    {listItems.map((item, itemIdx) => (
                        <li key={itemIdx} style={{ marginBottom: '4px' }}>
                            {renderInline(item)}
                        </li>
                    ))}
                </ol>
            );
            continue;
        }

        // 7. Regular Paragraph
        if (line.trim() === '') {
            i++;
            continue;
        }

        elements.push(
            <div
                key={`p-${i}`}
                style={{
                    margin: '6px 0',
                    fontSize: '13px',
                    lineHeight: '1.6',
                    color: isDark ? '#e0e0e0' : '#262626',
                }}
            >
                {renderInline(line)}
            </div>
        );
        i++;
    }

    return <div className="markdown-body-container">{elements}</div>;
};

export default MarkdownRenderer;
